import React, { useRef, useState } from 'react';
import router from 'next/router';
import Dropdown from 'react-bootstrap/Dropdown';
import useTranslation from 'next-translate/useTranslation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faSignOut } from '@fortawesome/free-solid-svg-icons';
import { APP_ROUTES } from '@constants/constants';
import { useIsSmallScreen } from '@/hooks/useIsSmallScreen';
import { SecurityScope } from '@/utils/acl-utils';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useAppSelector } from '@/configureStore';
import { selectUser } from '@/stores/currentUserApi';
import styles from './UserArea.module.scss';

const dropdownItems = [
  {
    label: 'Dashboard',
    href: APP_ROUTES.DASHBOARD,
    id: '{e7872171-04be-4d52-9f81-b46f20e500b4}'
  },
  {
    label: 'common:main-navigation.OrderLists',
    href: APP_ROUTES.ORDERLISTS,
    id: '{98035988-e47d-464a-9c1f-24b56d32c072}',
    security: SecurityScope.ORDER_LISTS_VIEW
  },
  {
    label: 'common:main-navigation.Manage',
    href: APP_ROUTES.MANAGE,
    id: '{c11758a6-2b4d-4c45-b6aa-94da08531048}',
    security: SecurityScope.ACCOUNT_UPDATE,
    Icon: () => <FontAwesomeIcon icon={faCog} className={'text-theme mr-2'} />
  },
  {
    label: 'common:actions.logout',
    href: APP_ROUTES.LOGOUT,
    id: '{82e43253-e867-487e-8034-51e02a8d9b47}',
    Icon: () => <FontAwesomeIcon icon={faSignOut} className={'text-theme mr-2'} />
  }
];

interface ICustomToggle {
  children: React.ReactNode;
  onClick: React.EventHandler<any>;
  className?: string;
  isOpen?: boolean;
}

const CustomToggle = React.forwardRef<HTMLButtonElement, ICustomToggle>(function CustomToggle({ children, onClick, isOpen }, ref) {
  const isSmallScreen = useIsSmallScreen();
  const { t } = useTranslation();

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    isSmallScreen ? router.push('/handlers/logoff.ashx') : onClick(e);
  };

  return (
    <>
      <button
        className={`btn btn-link d-flex ${styles.dropdownToggle}`}
        ref={ref}
        aria-haspopup="true"
        aria-expanded={isOpen}
        data-toggle="dropdown"
        type="button"
        onClick={handleClick}
      >
        <span className={`${styles.userName} text-sans-serif`}>
          <span className="fa fa-user mr-2"></span>
          <strong>{children}</strong>
          <div className="d-lg-none">{t('common:actions.logout')}</div>
        </span>

        <div className="d-none d-lg-inline">
          {isOpen && <span id="chevron" className="fa fa-angle-up"></span>}
          {!isOpen && <span id="chevron" className="fa fa-angle-down"></span>}
        </div>
      </button>
    </>
  );
});

export const UserDropdown = () => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => selectUser(state));

  const ref = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  useClickOutside(ref, () => setShowDropdown(false));

  return (
    <div className={styles.userDropdown}>
      <Dropdown ref={ref} drop="down" show={showDropdown} onToggle={() => setShowDropdown(!showDropdown)}>
        <Dropdown.Toggle as={CustomToggle} isOpen={showDropdown}>
          {user?.firstName}
        </Dropdown.Toggle>
        <Dropdown.Menu className={styles.dropdownMenu}>
          {dropdownItems.map((item, idx) => {
            const { label, Icon = null, href = null } = item;
            const dropdownItemProps = {
              ...(href ? { href } : {})
            };

            return (
              <React.Fragment key={idx}>
                <Dropdown.Item role="button" {...dropdownItemProps} className={styles.dropdownItem}>
                  {Icon && <Icon />}
                  {t(label)}
                </Dropdown.Item>
              </React.Fragment>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};
